<template>
  <div
    class="text-grey-900 gp-color-picker"
    :class="{
      'absolute top-0 mt-10 z-40 ': !expanded,
      'right-0': align !== 'left' && !rightSide && !expanded,
      'left-0': align === 'left' || (rightSide && !expanded),
    }"
    style="right: -1rem; box-shadow: 0 0 16px 0 #000"
    v-on-click-outside="closeMenu"
  >
    <div
      class="relative rounded border-solid border-grey-700 w-64 p-4"
      :class="{
        border: !expanded,
        'bg-grey-900': !expanded || floating,
        'bg-white': expanded && !floating,
      }"
    >
      <button
        v-if="immediateChange"
        class="absolute right-5 pointer-events-auto z-10 check-icon"
        @click="pushColor(value)"
      >
        <check-icon />
      </button>
      <button
        v-if="!floating"
        class="absolute right-5 pointer-events-auto z-10"
        @click="closeMenuDirect"
        style="margin-top: 2px"
      >
        <close-icon class="w-4 h-4 fill-current" />
      </button>
      <div class="flex flex-row justify-left mb-3">
        <div class="flex flex-row">
          <div class="bg-white rounded w-5 h-5 mr-1">
            <button
              style="backgroundcolor: #fff"
              class="
                rounded
                shadow
                hover:border-black
                w-5
                h-5
                focus:outline-none
                flex
                items-center
                justify-center
                relative
                overflow-hidden
              "
              @click="setValue('white', 1)"
            ></button>
          </div>
          <div class="bg-black rounded w-5 h-5 mr-1">
            <button
              style="backgroundcolor: #000"
              class="
                rounded
                shadow
                hover:border-black
                w-5
                h-5
                focus:outline-none
                flex
                items-center
                justify-center
                relative
                overflow-hidden
              "
              @click="setValue('black', 1)"
            ></button>
          </div>
          <div class="bg-white rounded w-5 h-5 mr-1">
            <button
              :style="{ backgroundColor: getButtonColor('transparent', true) }"
              class="
                rounded
                shadow
                hover:border-black
                w-5
                h-5
                focus:outline-none
                flex
                items-center
                justify-center
                relative
                overflow-hidden
              "
              @click="makeColorTransparent()"
            >
              <transparent-icon class="w-5 h-5 fill-current" />
            </button>
          </div>
        </div>
        <div class="relative text-white">
          <button
            class="
              hover:bg-grey-700
              text-grey-500
              border-grey-700 border-solid border
              rounded
              shadow
              w-auto
              h-5
              mb-1
              focus:outline-none
              flex
              items-center
              justify-center
              px-2
              text-xs
            "
            @mousedown.prevent="removeColor()"
          >
            <span>{{ $t('frontend_sites_color_picker_nocolor') }}</span>
          </button>
        </div>
      </div>
      <div class="flex flex-row w-full my-2">
        <gd-button
          v-for="ct in colorTypes"
          :key="ct"
          :dark="ct !== colorType"
          :primary="ct === colorType"
          size="large"
          :group-position="colorTypes.indexOf(ct) === 1 ? 'middle' : colorTypes.indexOf(ct) ? 'right' : 'left'"
          @click="setColorType(ct)"
          >{{ $t(`frontend_sites_color_picker_${ct}`) }}</gd-button
        >
      </div>
      <div v-if="colorType === 'spectrum'" class="flex flex-row inline-block">
        <verte
          ref="verte"
          v-model="colorValue"
          class="verte border-solid rounded-lg border-grey-700"
          picker="square"
          model="rgb"
          display="widget"
          :showHistory="null"
          @input="verteEnd"
          :enableAlpha="false"
        ></verte>
      </div>
      <div
        v-else-if="colorType === 'grid'"
        class="inline-flex rounded-lg overflow-hidden mb-4 justify-center"
      >
        <v-swatches
          inline
          swatches="text-advanced"
          v-model="colorValue"
        ></v-swatches>
      </div>
      <div v-else class="flex flex-row inline-block">
        <div class="flex flex-wrap mb-3">
          <div
            v-for="color in baseColors"
            :key="color"
            class="relative text-grey-500"
          >
            <div class="mr-1 mb-1">
              <button
                v-if="color !== 'transparent'"
                :style="{ backgroundColor: getButtonColor(color, true) }"
                class="
                  rounded
                  shadow
                  hover:border-black
                  w-5
                  h-5
                  focus:outline-none
                  flex
                  items-center
                  justify-center
                "
                @mouseenter="openVariations($event, color)"
                @click="openVariations($event, color)"
              >
                <check-icon
                  v-if="value && value.indexOf(color) !== -1"
                  class="w-4 h-4 fill-current"
                />
              </button>
            </div>
            <div
              v-if="variationsOpen === color"
              v-on-click-outside="closeMenu"
              class="absolute z-10 p-1 rounded shadow flex flex-wrap"
              :class="{
                'bg-white': expanded && !floating,
                'bg-grey-900 border border-grey-700': !expanded || floating,
              }"
              style="top: -0.25rem; left: -0.25rem"
              @mouseleave="closeVariations()"
            >
              <button
                v-for="variant in getColorVariations(color)"
                :key="variant"
                :style="{ backgroundColor: getButtonColor(variant) }"
                class="
                  rounded
                  hover:border-2 hover:border-black
                  w-5
                  h-5
                  mb-1
                  focus:outline-none
                "
                @click="selectVariation($event, variant)"
              ></button>
            </div>
          </div>
        </div>
      </div>
      <div
        v-if="recentColorCookiesSpecific.length"
        class="mb-2 border-grey-800 border-t"
      >
        <label class="text-xs tracking-wide text-grey-100 my-1 block">{{
          $t('frontend_sites_color_picker_recents')
        }}</label>
        <div class="flex flex-wrap gap-1">
          <button
            v-for="color in recentColorCookiesSpecific.slice(0, 9)"
            :key="color"
            class="rounded w-5 h-5 focus:outline-none"
            :style="{ backgroundColor: color }"
            @click="setValue(color, opacityValue)"
          ></button>
        </div>
      </div>
      <div v-if="cookiesOrigin" class="mb-4 border-grey-800 border-t">
        <div class="flex justify-between">
          <label class="text-xs tracking-wide text-grey-100 my-1 block"
            >{{ $t('frontend_sites_color_picker_favorites') }} <star
          /></label>
          <div v-if="lastColor">
            <button
              v-if="ableToFavorite"
              class="
                hover:bg-grey-700
                text-grey-500
                border-grey-700 border-solid border
                rounded
                shadow
                w-auto
                h-5
                mb-1
                mt-1
                focus:outline-none
                flex
                items-center
                justify-center
                px-2
                text-xs
              "
              @mousedown.prevent="addColorToFavorite()"
            >
              <span>+</span>
            </button>
            <button
              v-else
              class="
                hover:bg-grey-700
                text-grey-500
                border-grey-700 border-solid border
                rounded
                shadow
                w-auto
                h-5
                mb-1
                mt-1
                focus:outline-none
                flex
                items-center
                justify-center
                px-2
                text-xs
              "
              @mousedown.prevent="removeColorFromFavorite()"
            >
              <span>-</span>
            </button>
          </div>
        </div>
        <div class="flex flex-wrap gap-1 border-b border-grey-800 pb-2">
          <button
            v-for="color in favoriteColorCookiesSpecific"
            :key="color"
            class="rounded w-5 h-5 focus:outline-none"
            :style="{ backgroundColor: color }"
            @click="setValue(color, opacityValue)"
          ></button>
        </div>
      </div>
      <div
        class="mb-4 transparency-slider relative"
        :class="{ disabled: !(value && value !== 'transparent') }"
        ref="transparency"
      >
        <label class="text-xs tracking-wide text-grey-100 mb-1 block">{{
          $t('frontend_sites_color_picker_transparency')
        }}</label>
        <span class="alert-tooltip" v-if="transparencyAlert">{{
          $t('frontend_sites_color_picker_transparency_alert')
        }}</span>
        <gd-slider
          v-model="opacitySlider"
          min="0"
          :max="steps"
          step="1"
          :format-value="formatValue"
          :readonly="false"
          dark
        />
      </div>
      
      <div
        class="
          flex flex-wrap
          w-full
          border
          rounded-lg
          text-white
          border-grey-700
          overflow-hidden
          radio-label
          rounded-b-none
        "
      >
        <label
          class="
            flex-grow
            p-2
            w-4
            border-r border-grey-700
            cursor-pointer
            text-center
          "
          :class="{ active: currentColorFormat === 'HEX' }"
          @click="currentColorFormat = 'HEX'"
          >HEX</label
        >
        <label
          class="
            flex-grow
            p-2
            w-4
            border-r border-grey-700
            cursor-pointer
            text-center
          "
          :class="{ active: currentColorFormat === 'RGB' }"
          @click="currentColorFormat = 'RGB'"
          >RGB</label
        >
        <label
          class="
            flex-grow
            p-2
            w-4
            border-r border-grey-700
            cursor-pointer
            text-center
          "
          :class="{ active: currentColorFormat === 'HSL' }"
          @click="currentColorFormat = 'HSL'"
          >HSL</label
        >
      </div>
      <div class="bg-grey-800 px-4 py-4 rounded-b-lg flex justify-between">
        <div
          v-if="currentColorFormat === 'RGB'"
          class="flex justify-around mr-2"
        >
          <div>
            <input
              pattern="/\b([01]?[0-9][0-9]?|2[0-4][0-9]|25[0-5])"
              type="number"
              :max="255"
              v-model="inputColor.rgb[0]"
              :class="fieldClasses"
              class="w-12"
              placeholder="R"
            />
          </div>
          <div>
            <input
              type="number"
              :max="255"
              v-model="inputColor.rgb[1]"
              :class="fieldClasses"
              class="w-12 mx-1"
              placeholder="G"
            />
          </div>
          <div>
            <input
              type="number"
              :max="255"
              v-model="inputColor.rgb[2]"
              :class="fieldClasses"
              class="w-12"
              placeholder="B"
            />
          </div>
        </div>

        <div
          v-if="currentColorFormat === 'HEX'"
          class="flex justify-center mr-2"
        >
          <div>
            <input
              v-model="inputColor.hex"
              :class="fieldClasses"
              class="w-21"
              placeholder="HEX"
            />
          </div>
        </div>

        <div
          v-if="currentColorFormat === 'HSL'"
          class="flex justify-around mr-2"
        >
          <div>
            <input
              type="number"
              :max="360"
              v-model="inputColor.hsl[0]"
              :class="fieldClasses"
              class="w-12"
              placeholder="H"
            />
          </div>
          <div>
            <input
              type="number"
              :max="100"
              v-model="inputColor.hsl[1]"
              :class="fieldClasses"
              class="mx-1 w-12"
              placeholder="S"
            />
          </div>
          <div>
            <input
              :class="fieldClasses"
              class="w-12"
              type="number"
              :max="100"
              v-model="inputColor.hsl[2]"
              placeholder="L"
            />
          </div>
        </div>
        <gd-button
          :disabled="shouldDisableColors"
          primary
          size="icon"
          @click="applyColorValues(currentColorFormat)"
        >
          <i class="fas fa-check" />
        </gd-button>
      </div>
    </div>
  </div>
</template>
<style lang="scss">
@import '../assets/scss/colorpicker-styling.scss';
.radio-label {
  border: none;
  background-color: transparent !important;

  label {
    border: none;
    font-weight: bold;
    font-size: 12px;
    margin: 0 4px;
    border-radius: 0.5rem 0.5rem 0 0;
    background-color: #1f2241;

    &:first-child {
      margin-left: 0;
    }

    &:last-child {
      margin-right: 0;
    }

    &.active {
      background-color: #292b49;
    }
  }
}

.verte__controller {
  display: none;
}

div.flex.flex-row.ml-4.mb-4.inline.block > div > div > div {
  background-color: #191c3c;
  color: #fff;
}

div.verte__inputs > input {
  color: #000;
  border-radius: 0.25rem;
}

.verte__menu {
  background-color: #191c3c;
  width: 222px;
  box-shadow: none;
}

.verte-picker__origin {
  border-radius: 0.25rem;
}

.verte-picker__slider {
  width: 100%;
  margin: 10px auto;
}

.w-21 {
  width: 9rem;
}

input[type='number']::-webkit-inner-spin-button,
input[type='number']::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.transparency-slider {
  &.disabled {
    cursor: not-allowed;
    opacity: 0.5;
    filter: grayscale(1);
    position: relative;

    &:before {
      position: absolute;
      top: 0;
      left: 0;
      z-index: 1;
      content: '';
      display: block;
      width: 100%;
      height: 100%;
    }
  }

  &.alert {
    animation: pulse 1.4s ease 0s 1 normal forwards;
  }

  @keyframes pulse {
    0% {
      animation-timing-function: ease-out;
      transform: scale(1);
      transform-origin: center center;
    }

    10% {
      animation-timing-function: ease-in;
      transform: scale(0.91);
    }

    17% {
      animation-timing-function: ease-out;
      transform: scale(0.98);
    }

    33% {
      animation-timing-function: ease-in;
      transform: scale(0.87);
    }

    45% {
      animation-timing-function: ease-out;
      transform: scale(1);
    }
  }
}

.vue-swatches__container {
  background: none !important;

  .vue-swatches__row {
    > div {
      max-width: 22px;
      max-height: 22px;

      .vue-swatches__check__circle {
        height: 16px;
        width: 16px;
      }
    }
  }
}

span.alert-tooltip {
  background-color: #646464;
  font-size: 11px;
  padding: 4px 6px 2px;
  border-radius: 3px;
  color: white;
  position: absolute;
  top: 0;
  width: 100%;
  text-align: center;

  &:after {
    content: '';
    width: 0;
    height: 0;
    border-style: solid;
    border-width: 7px 5px 0 5px;
    border-color: #646464 transparent transparent transparent;
    position: absolute;
    top: 22px;
    right: 52px;
  }
}

.check-icon {
  margin-top: -1px;
  margin-right: 30px;

  svg {
    width: 20px;
    height: 20px;
  }
}

.gp-color-picker {
  label {
    text-align: left;
    svg {
      width: 12px;
      height: 17px;
      float: right;
      margin-left: 5px;
      margin-bottom: 3px;
    }
  }
}
</style>
<script>
import GdButton from '@groovepages/gd-ui-kit/components/Button';
import { MD5 } from 'crypto-js';
import cookies from 'js-cookie';
import { debounce } from 'lodash-es';
import parse from 'parse-color';
import 'verte/dist/verte.css';
import VSwatches from 'vue-swatches';
import 'vue-swatches/dist/vue-swatches.css';
import CheckIcon from '../assets/check-icon-2.svg';
import CloseIcon from '../assets/close-icon.svg';
import TransparentIcon from '../assets/transparent-icon.svg';
import Star from '../assets/white-star.svg';
import directives from '../directives';
import GdSlider from './Slider';
import Verte from './Verte';

export default {
  name: 'ColorPickerMenu',
  components: {
    GdSlider,
    CloseIcon,
    TransparentIcon,
    Verte,
    GdButton,
    VSwatches,
    CheckIcon,
    Star
  },
  directives,
  props: {
    colors: {
      default: () => ({
        transparent: 'transparent',
        'primary-darkest': 'hsla(213, 90%, 10%, 1.00)',
        'primary-darker': 'hsla(213, 95%, 25%, 1.00)',
        'primary-dark': 'hsla(213, 95%, 35%, 1.00)',
        primary: 'hsla(213, 100%, 59%, 1.00)',
        'primary-light': 'hsla(213, 100%, 70%, 1.00)',
        'primary-lighter': 'hsla(213, 100%, 83%, 1.00)',
        'primary-lightest': 'hsla(213, 100%, 93%, 1.00)',
        'secondary-darkest': 'hsla(342, 55%, 10%, 1.00)',
        'secondary-darker': 'hsla(342, 65%, 25%, 1.00)',
        'secondary-dark': 'hsla(342, 65%, 38%, 1.00)',
        secondary: 'hsla(342, 75%, 57%, 1.00)',
        'secondary-light': 'hsla(342, 75%, 65%, 1.00)',
        'secondary-lighter': 'hsla(342, 75%, 80%, 1.00)',
        'secondary-lightest': 'hsla(342, 85%, 93%, 1.00)',
        'grey-darkest': 'hsla(213, 40%, 10%, 1.00)',
        'grey-darker': 'hsla(213, 30%, 18%, 1.00)',
        'grey-dark': 'hsla(202, 20%, 28%, 1)',
        grey: 'hsla(213, 15%, 54%, 1.00)',
        'grey-light': 'hsla(213, 15%, 72%, 1.00)',
        'grey-lighter': 'hsla(213, 15%, 85%, 1.00)',
        'grey-lightest': 'hsla(213, 15%, 95%, 1.00)',
        'red-darkest': 'hsla(0, 90%, 12%, 1.00)',
        'red-darker': 'hsla(0, 80%, 25%, 1.00)',
        'red-dark': 'hsla(0, 80%, 48%, 1.00)',
        red: 'hsla(0, 99%, 64%, 1.00)',
        'red-light': 'hsla(0, 99%, 71%, 1.00)',
        'red-lighter': 'hsla(0, 99%, 83%, 1.00)',
        'red-lightest': 'hsla(0, 99%, 93%, 1.00)',
        'green-darkest': 'hsla(143, 69%, 10%, 1.00)',
        'green-darker': 'hsla(143, 79%, 20%, 1.00)',
        'green-dark': 'hsla(143, 79%, 30%, 1.00)',
        green: 'hsla(143, 79%, 45%, 1.00)',
        'green-light': 'hsla(143, 75%, 55%, 1.00)',
        'green-lighter': 'hsla(143, 75%, 70%, 1.00)',
        'green-lightest': 'hsla(143, 80%, 80%, 1.00)',
        'yellow-darkest': 'hsla(20, 90%, 20%, 1.00)',
        'yellow-darker': 'hsla(30, 100%, 30%, 1.00)',
        'yellow-dark': 'hsla(40, 100%, 40%, 1.00)',
        yellow: 'hsla(46, 100%, 57%, 1.00)',
        'yellow-light': 'hsla(46, 95%, 69%, 1.00)',
        'yellow-lighter': 'hsla(46, 95%, 79%, 1.00)',
        'yellow-lightest': 'hsla(46, 90%, 92%, 1.00)',
        purple: 'hsla(227, 49%, 55%, 1.00)',
        indigo: 'hsla(225, 63%, 49%, 1.00)',
        blue: 'hsla(209, 63%, 43%, 1.00)'
      })
    },
    opacities: {
      default: () => [...Array(101).keys()].map(value => value / 100)
    },
    value: {},
    expanded: {},
    rightSide: {},
    floating: {
      default: false
    },
    align: {
      type: String,
      default: null
    },
    immediateChange: {
      type: Boolean,
      default: false
    },
    cookiesOrigin: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      variationsOpen: null,
      currentColorFormat: 'HEX',
      colorType: 'palette',
      colorTypes: ['palette','grid', 'spectrum'],
      inputColor: {
        rgb: [],
        hex: [],
        hsl: []
      },
      transparencyAlert: false,
      fieldClasses:
        'px-2 border border-solid border-grey-700 bg-grey-800 text-white rounded text-sm h-8 flex items-center justify-center text-center focus:outline-none',
      colorString: null,
      lastColor: null,
      recentColorCookies: {},
      recentColorCookiesSpecific: [],
      favoriteColorCookies: {},
      favoriteColorCookiesSpecific: []
    };
  },
  mounted() {
    this.setInputColor(this.value);
    this.loadcolorCookies(this.cookiesOrigin);
  },
  computed: {
    ableToFavorite() {
      return this.lastColor && !this.favoriteColorCookiesSpecific.includes(this.lastColor);
    },
    colorValue: {
      get() {
        this.enablePicker();
        if (this.value === 'transparent' || this.value === null) return 'transparent';
        return parse(this.value).hex;
      },
      set(value) {
        this.setValue(value, this.opacityValue, false);
      }
    },
    opacitySlider: {
      get() {
        return Math.round(this.opacityValue * 100);
      },
      set(value) {
        this.setValue(this.colorValue, value / 100, true);
      }
    },
    valueStrings() {
      const strings = {};
      for (const color in this.colors) {
        strings[color] = parse(this.colors[color]).hex;
      }
      return strings;
    },
    opacityValue() {
      if (this.value === 'transparent' || !this.value || !parse(this.value).rgba) return 1;
      return parse(this.value).rgba[3];
    },
    steps() {
      return this.opacities.length - 1;
    },
    baseColors() {
      return Object.keys(this.colors).filter(
        color => (color.indexOf('-') === -1 || color.indexOf('default') !== -1) && color !== 'transparent'
      );
    },
    shouldDisableColors() {
      return !(
        this.inputColor?.rgb?.length ||
        this.inputColor?.hex?.length ||
        this.inputColor?.hsl?.length
      );
    }
  },
  methods: {
    verteEnd: debounce(function() {
      this.addColorCookies(this.lastColor, 'recent');
    }, 1000),
    loadcolorCookies(origin) {
      if (origin) {
        this.recentColorCookies = cookies.get(MD5('colorpicker_recent')) ? JSON.parse(cookies.get(MD5('colorpicker_recent'))) : {};

        if (this.recentColorCookies && this.recentColorCookies[MD5(origin)])
          this.recentColorCookiesSpecific = this.recentColorCookies[MD5(origin)];

        this.favoriteColorCookies = cookies.get(MD5('colorpicker_favorite')) ? JSON.parse(cookies.get(MD5('colorpicker_favorite'))) : {};

        if (this.favoriteColorCookies && this.favoriteColorCookies[MD5(origin)])
          this.favoriteColorCookiesSpecific = this.favoriteColorCookies[MD5(origin)];
      }
    },
    addColorCookies(color, target) {
      if (this.cookiesOrigin && target) {
        const jsonColorData = {};
        let colorCookies = {};

        if (target === 'recent') {
          colorCookies = this.recentColorCookies;
          if (!this.recentColorCookiesSpecific.includes(color)) this.recentColorCookiesSpecific.unshift(color);
          jsonColorData[MD5(this.cookiesOrigin)] = this.recentColorCookiesSpecific.slice(0, 9);
        } else if (target === 'favorite') {
          colorCookies = this.favoriteColorCookies;
          if (!this.favoriteColorCookiesSpecific.includes(color)) this.favoriteColorCookiesSpecific.push(color);
          jsonColorData[MD5(this.cookiesOrigin)] = this.favoriteColorCookiesSpecific;
        }

        cookies.set(MD5(`colorpicker_${target}`), {
          ...colorCookies,
          ...jsonColorData
        });
      }
    },
    removeColorCookies(color, target) {
      if (this.cookiesOrigin && target) {
        const jsonColorData = {};
        let colorCookies = {};

        if (target === 'recent') {
          // Currently recent colors has no need to be deleted
        } else if (target === 'favorite') {
          colorCookies = this.favoriteColorCookies;
          this.favoriteColorCookiesSpecific = this.favoriteColorCookiesSpecific.filter(item => item !== color);
          jsonColorData[MD5(this.cookiesOrigin)] = this.favoriteColorCookiesSpecific;
        }

        cookies.set(MD5(`colorpicker_${target}`), {
          ...colorCookies,
          ...jsonColorData
        });
      }
    },
    addColorToFavorite() {
      this.addColorCookies(this.lastColor, 'favorite');
    },
    removeColorFromFavorite() {
      this.removeColorCookies(this.lastColor, 'favorite');
    },
    setValue(color, opacity = 1, changingOpacity = false) {
      this.setInputColor(color);
      const { rgb } = parse(color);
      const lastColor = `rgb(${rgb[0]}, ${rgb[1]}, ${rgb[2]})`;
      const colorString = `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, ${opacity})`;

      this.$emit('input', colorString);
      this.$emit('change', colorString);

      this.colorString = colorString;
      this.lastColor = lastColor;

      if (this.colorType === 'grid' || 'palette') this.addColorCookies(lastColor, 'recent');

      if (opacity < 1 && !changingOpacity && !this.transparencyAlert) {
        this.transparencyAlert = true;

        setTimeout(() => {
          this.transparencyAlert = false;
        }, 2000);
      }
    },
    pushColor() {
      this.$emit('push-color', this.colorString);
    },
    enablePicker() {
      setTimeout(() => {
        if (this.$refs.verte) {
          this.$refs.verte.$children.forEach(child => {
            child.preventEcho = false;
          });
        }
      });
    },

    formatValue(value) {
      const opacity = (1 - this.opacities[value]) * 100;
      return `${Math.round(opacity)}%`;
    },
    removeColor() {
      this.setInputColor(null);
      this.$emit('input', null);
      this.$emit('change', null);
    },
    openVariations(e, base) {
      e.preventDefault();
      if (this.getColorVariations(base).length > 1) this.variationsOpen = base;
      else this.selectVariation(e, base);
    },
    closeVariations() {
      this.variationsOpen = null;
    },
    selectVariation(e, variation) {
      if (e.type === 'click') {
        this.variationsOpen = null;
        this.setValue(this.colors[variation], this.opacityValue);
      }
    },
    getColorVariations(color) {
      return Object.keys(this.colors).filter(variant => variant.indexOf(color) !== -1);
    },
    getButtonColor(color, baseButton) {
      const comparison = parse(this.value).hex;
      let valueString = '';
      if (baseButton) {
        for (const string in this.valueStrings) {
          if (this.valueStrings[string] === comparison) {
            valueString = string;
          }
        }
        if (valueString.indexOf(color) !== -1) {
          return this.colors[valueString];
        }
      }
      return this.colors[color];
    },
    closeMenu() {
      this.$emit('close');
    },
    closeMenuDirect() {
      this.$emit('close');
      this.$emit('close-direct');
    },
    makeColorTransparent() {
      this.setValue('rgba(256,256,256,0)', 0, true);
    },
    formatRGB() {
      this.inputColor.rgb = this.inputColor.rgb.map(c => {
        if (!c || c < 0) c = 0;
        if (c > 255) c = 255;
        return parseInt(c);
      });
    },
    formatHEX() {
      this.inputColor.hex = this.inputColor.hex.replace(/[^#A-Fa-f0-9]/g, '');
    },
    formatHSL() {
      this.inputColor.hsl = this.inputColor.hsl.map((c, index) => {
        if (index === 0) {
          if (!c || c < 0 || c > 359) c = 0;
        } else if (!c || c < 0 || c > 100) c = 100;
        return parseInt(c);
      });
    },
    setInputColor(color) {
      if (color) {
        this.inputColor.rgb = parse(color).rgb;
        this.inputColor.hex = parse(color).hex;
        this.inputColor.hsl = parse(color).hsl;
        return;
      }
      this.inputColor.rgb = [];
      this.inputColor.hex = [];
      this.inputColor.hsl = [];
    },
    applyColorValues(format) {
      let colorString = '';
      if (format === 'RGB') {
        this.formatRGB();
        colorString = `rgb(${this.inputColor.rgb[0]}, ${this.inputColor.rgb[1]}, ${this.inputColor.rgb[2]})`;
      } else if (format === 'HEX') {
        this.formatHEX();
        colorString = `${this.inputColor.hex}`;
      } else {
        this.formatHSL();
        colorString = `hsl(${this.inputColor.hsl[0]}, ${this.inputColor.hsl[1]}%, ${this.inputColor.hsl[2]}%)`;
      }
      this.setValue(colorString, this.opacityValue);
    },
    setColorType(ct) {
      this.colorType = ct;
    }
  }
};
</script>
